import React, { useState } from "react";
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from "react-native";
import { useTheme } from "../../context/ThemeContext";
import MemberDirectory from "./components/MemberDirectory";
import Discussions from "./components/Discussions";
import ReadingUpdates from "./components/ReadingUpdates";
import Messages from "./components/Messages";
import { Ionicons } from "@expo/vector-icons";
import { CommunityPageType } from "./index";
import { useContent } from "../../context/ContentContext";
import { withComponentEditor } from "../BaseComponent";
const componentType = "communityPage";
const CommunityPage = ({ content = {}, onComponentSelect }) => {
	const [activeTab, setActiveTab] = useState("members");
	const theme = useTheme();
	const { fetchContent, contentSnapshot } = useContent();
	const pageContent = fetchContent(componentType) || {};
	// Merge with default content
	const defaultContent = CommunityPageType.getDefaultContent();
	const mergedContent = {
		...defaultContent,
		...content,
	};
	const handleEditor = () => {
		if (onComponentSelect) {
			onComponentSelect({
				componentType,
				sections: [
					{
						id: "general",
						title: "General Settings",
						properties: ["pageTitle", "pageIcon"],
					},
				],
				properties: {
					// General Settings
					pageTitle: {
						store: "content",
						type: "text",
						label: "Page Title",
						description: "The title of what your product page will be about. (e.g. Products, Books..., etc.)",
						value: pageContent.pageTitle,
					},
					pageIcon: {
						store: "content",
						type: "icon",
						label: "Page Icon",
						description: "The icon of what your product page will be about. (e.g. Products, Books..., etc.)",
						value: pageContent.pageIcon,
					},
				},
			});
		}
	};
	const renderContent = () => {
		switch (activeTab) {
			case "members":
				return <MemberDirectory members={mergedContent.members} />;
			case "discussions":
				return <Discussions discussions={mergedContent.discussions} />;
			case "updates":
				return <ReadingUpdates updates={mergedContent.readingUpdates} />;
			case "messages":
				return <Messages messages={mergedContent.messages} members={mergedContent.members} />;
			default:
				return null;
		}
	};

	const tabs = [
		{ id: "members", icon: "people-outline", label: "Members" },
		{ id: "discussions", icon: "chatbubbles-outline", label: "Discussions", badge: 3 },
		{ id: "messages", icon: "mail-outline", label: "Messages", badge: 2 },
		{ id: "updates", icon: "bar-chart-outline", label: "Reading Updates" },
	];

	const styles = StyleSheet.create({
		container: {
			flex: 1,
			backgroundColor: theme.colors.background,
		},
		header: {
			padding: theme.spacing.md,
			backgroundColor: theme.colors.systemBackground.primary,
			borderBottomWidth: 1,
			borderBottomColor: theme.colors.separator,
		},
		title: {
			...theme.typography.title1,
			color: theme.colors.text,
			marginBottom: theme.spacing.md,
		},
		tabsContainer: {
			paddingRight: theme.spacing.md,
		},
		tab: {
			marginRight: theme.spacing.sm,
			paddingVertical: theme.spacing.sm,
			paddingHorizontal: theme.spacing.md,
			borderRadius: theme.borderRadius.pill,
			backgroundColor: theme.colors.gray[6],
		},
		tabContent: {
			flexDirection: "row",
			alignItems: "center",
			gap: theme.spacing.sm,
		},
		activeTab: {
			backgroundColor: theme.colors.primary,
		},
		tabText: {
			...theme.typography.subhead,
			color: theme.colors.textSecondary,
		},
		activeTabText: {
			color: theme.colors.systemBackground.primary,
			fontWeight: "600",
		},
		content: {
			flex: 1,
			padding: theme.spacing.md,
		},
		badge: {
			backgroundColor: theme.colors.error,
			borderRadius: theme.borderRadius.pill,
			paddingHorizontal: 6,
			paddingVertical: 2,
			minWidth: 20,
			alignItems: "center",
		},
		badgeText: {
			...theme.typography.caption2,
			color: theme.colors.systemBackground.primary,
			fontWeight: "600",
		},
	});

	return (
		<View style={styles.container}>
			<View style={styles.header}>
				<Text onPress={handleEditor} style={styles.title}>
					{pageContent.pageTitle}
				</Text>
				<ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.tabsContainer}>
					{tabs.map((tab) => (
						<TouchableOpacity key={tab.id} style={[styles.tab, activeTab === tab.id && styles.activeTab]} onPress={() => setActiveTab(tab.id)}>
							<View style={styles.tabContent}>
								<Ionicons name={tab.icon} size={20} color={activeTab === tab.id ? theme.colors.systemBackground.primary : theme.colors.textSecondary} />
								<Text style={[styles.tabText, activeTab === tab.id && styles.activeTabText]}>{tab.label}</Text>
								{tab.badge && (
									<View style={styles.badge}>
										<Text style={styles.badgeText}>{tab.badge}</Text>
									</View>
								)}
							</View>
						</TouchableOpacity>
					))}
				</ScrollView>
			</View>

			<ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
				{renderContent()}
			</ScrollView>
		</View>
	);
};

export default withComponentEditor(CommunityPage);
